import type {ItemDefinition, Range, RowDefinition} from '@Libraries/timeline/types';
import type {CSSProperties} from 'react';
import {updateTimelineWidthPerSecond} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import {isOverlapping} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-subtitle-item/timeline-subtitle-item.helper';
import type {DeepPartial} from '@/global';

export const TIMELINE_DESIGN_ITEM_ID = 'design-seek';

export enum TimelineItemTypeId {
  Design = 'design-timeline',
  Audio = 'audio-timeline',
  Transcript = 'transcript-timeline',
}

export const generateTimelineItems = (): ItemDefinition[] => {
  return [...generateDesignSeekItem(), ...generateAudioItems(), ...generateSubtitleItems()];
};

export const generateDesignSeekItem = (): ItemDefinition[] => {
  const items: ItemDefinition[] = [];
  const orderOfPages = window.posterEditor?.whiteboard?.pages.pageOrder;
  const pagesHashMap = window.posterEditor?.whiteboard?.pages.pagesHashMap;
  const leeway = 0.01;

  if (!orderOfPages || !pagesHashMap) {
    return items;
  }

  let startTime = 0;
  for (const pageId of orderOfPages) {
    const pageDuration = pagesHashMap[pageId].getDuration();
    items.push({
      id: pageId,
      rowId: TimelineItemTypeId.Design,
      span: {
        start: startTime + leeway,
        end: startTime + pageDuration,
      },
    });
    startTime += pageDuration;
  }

  return items;
};

export const generateSubtitleItems = (): ItemDefinition[] => {
  const items: ItemDefinition[] = [];
  const whiteboard = window.posterEditor?.whiteboard;
  const pageOrder = whiteboard?.pages.pageOrder;
  const pagesHashMap = whiteboard?.pages.pagesHashMap;
  if (!whiteboard || !pageOrder || !pagesHashMap) {
    return items;
  }

  const pageStartOffsets: Record<string, number> = {};
  let cumulative = 0;
  for (const pid of pageOrder) {
    pageStartOffsets[pid] = cumulative;
    cumulative += pagesHashMap[pid].getDuration();
  }

  for (const pid of pageOrder) {
    const page = pagesHashMap[pid];
    const transcriptItems = page.items.getAllTranscriptItems();
    if (!transcriptItems || Object.keys(transcriptItems).length === 0) {
      continue;
    }

    const pageStartOffset = pageStartOffsets[pid];
    for (const [, item] of Object.entries(transcriptItems)) {
      for (const [, subtitleItem] of Object.entries(item.subtitlesHashmap)) {
        items.push({
          id: subtitleItem.subtitleUID,
          rowId: `${TimelineItemTypeId.Transcript}_${item.uid}`,
          disabled: false,
          span: {
            start: pageStartOffset + subtitleItem.startTime,
            end: pageStartOffset + subtitleItem.endTime,
          },
          data: {
            transcriptId: item.uid,
            pageId: pid,
          },
        });
      }

      items.push({
        id: `subtitle-display`,
        rowId: `${TimelineItemTypeId.Transcript}_${item.uid}`,
        disabled: false,
        span: {
          start: pageStartOffset,
          end: pageStartOffset,
        },
        data: {
          transcriptId: item.uid,
          isDisplayOnly: true,
        },
      });
    }
  }

  return items;
};

export const generateAudioItems = (): ItemDefinition[] => {
  const items: ItemDefinition[] = [];
  const audioHashmap = window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap;
  if (!audioHashmap) {
    return [];
  }
  const keys = Object.keys(audioHashmap);

  for (const eachKey of keys) {
    if (audioHashmap) {
      const item: ItemDefinition = {
        id: audioHashmap[eachKey].uid,
        rowId: TimelineItemTypeId.Audio,
        disabled: false,
        span: {
          start: audioHashmap[eachKey].onPosterStartTime,
          end: audioHashmap[eachKey].onPosterStartTime + audioHashmap[eachKey].audioPlayer.getPlaybackDuration(),
        },
      };
      items.push(item);
    }
  }

  return items;
};

export const getTimelineItemTransformProperty = (itemStyle: CSSProperties): string => {
  if (!itemStyle.transform) {
    return '';
  }

  const result = itemStyle.transform.substring(itemStyle.transform.indexOf('(') + 1, itemStyle.transform.lastIndexOf(')'));
  const splittedResult = result.split(',');
  return `translate3d(${splittedResult[0]} , 0px , ${splittedResult[2]})`;
};

export const generateTranscriptItemRows = (): RowDefinition[] => {
  const whiteboard = window.posterEditor?.whiteboard;
  if (!whiteboard) {
    return [];
  }
  const pageOrder = whiteboard.pages.pageOrder;
  const pagesHashMap = whiteboard.pages.pagesHashMap;
  if (!pageOrder || !pagesHashMap) {
    return [];
  }

  const rows: RowDefinition[] = [];
  for (const pid of pageOrder) {
    const page = pagesHashMap[pid];
    const transcriptIds = page.items.getTranscriptItemIds();
    if (!transcriptIds || transcriptIds.length === 0) {
      continue;
    }
    for (const tId of transcriptIds) {
      rows.push({
        id: `${TimelineItemTypeId.Transcript}_${tId}`,
        disabled: false,
      });
    }
  }
  return rows;
};

export const zoomTimelineIn = (currentWidthPerSecond: number): void => {
  const scale = currentWidthPerSecond < 40 ? 1.4 : 1.1;
  const value = scale * currentWidthPerSecond;
  window.PMW.redux.store.dispatch(updateTimelineWidthPerSecond(value));
};

export const zoomTimelineOut = (currentWidthPerSecond: number): void => {
  const scale = currentWidthPerSecond < 56 ? 1.4 : 1.1;
  const value = currentWidthPerSecond / scale;

  window.PMW.redux.store.dispatch(updateTimelineWidthPerSecond(value));
};

export const fireGAEventForTimelineOpen = (): void => {
  const isMobile = isEditorMobileVariant();
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_OPEN, {
    [GA4EventParamName.TYPE]: isMobile ? 'mobile' : 'web',
  });
};

export const fireGaEventForTimelineOpenFromBottomBar = (): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_OPEN_BOTTOM_BAR);
};

export const fireGaEventForTimelineOpenFromAudioIcon = (): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_OPEN_AUDIO_ICON);
};

export const isAValidAudioItem = (id: string): boolean => {
  const audioItemHashmap = window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap;

  return !!audioItemHashmap?.[id];
};

export const isAValidTranscriptSubtitleItem = (id: string): boolean => {
  const item = window.posterEditor?.whiteboard?.getItemForId(id);

  if (!item) {
    return false;
  }

  return item.isTranscript();
};

export const onTranscriptItemResizeEnd = async (updatedRange: Range, subtitleId: string): Promise<void> => {
  const transcriptItem = window.posterEditor?.whiteboard?.getTranscriptItemFromSubtitleId(subtitleId);
  if (!transcriptItem) {
    return;
  }

  const subtitle = transcriptItem.subtitlesHashmap[subtitleId];
  const pageStartTime = transcriptItem.getPageStartTime();
  const pageDuration = transcriptItem.page.getDuration();

  let relativeRange: Range = {
    start: updatedRange.start - pageStartTime,
    end: updatedRange.end - pageStartTime,
  };

  if (relativeRange.start < 0) {
    relativeRange = {start: 0, end: relativeRange.end - relativeRange.start};
  }
  if (relativeRange.start > pageDuration) {
    relativeRange = {start: pageDuration - 1, end: pageDuration};
  }
  if (relativeRange.end > pageDuration && relativeRange.start < pageDuration) {
    relativeRange = {start: relativeRange.start, end: pageDuration};
  }
  relativeRange = {start: Math.min(pageDuration - 1, relativeRange.start), end: relativeRange.end};

  const sortedIds: [string, number, number][] = [];
  for (const [key, item] of Object.entries(transcriptItem.subtitlesHashmap)) {
    if (key !== subtitleId) {
      sortedIds.push([key, item.startTime, item.endTime]);
    }
  }
  sortedIds.sort((a, b) => a[1] - b[1]);

  let overlap = false;
  const overlapIds: string[] = [];
  for (const eachItem of sortedIds) {
    if (isOverlapping(relativeRange, eachItem[1], eachItem[2])) {
      overlapIds.push(eachItem[0]);
      overlap = true;
    }
  }

  if (!overlap) {
    await subtitle.updateFromObject({
      startTime: relativeRange.start,
      endTime: relativeRange.end,
      hasUserEdited: true,
    });
    return;
  }

  if (overlapIds.length < 1) {
    return;
  }

  const firstOverlappingItem = transcriptItem.subtitlesHashmap[overlapIds[0]];
  const overlapPercentage = findOverlappingPercentageWithRespectToStart(firstOverlappingItem.startTime, firstOverlappingItem.endTime, relativeRange);
  if (overlapPercentage < 50) {
    const durationForSubtitle = subtitle.endTime - subtitle.startTime;
    const subtitleNewEndTime = firstOverlappingItem.startTime - 0.01;
    const subtitleNewStartTime = subtitleNewEndTime - durationForSubtitle;
    if (subtitleNewStartTime <= 0 || subtitleNewEndTime < 0) {
      return;
    }

    const itemBehindIndex = sortedIds.map((item) => item[0]).indexOf(overlapIds[0]) - 1;
    if (
      itemBehindIndex >= 0 &&
      transcriptItem.subtitlesHashmap[sortedIds[itemBehindIndex][0]] &&
      isOverlapping(
        {start: subtitleNewStartTime, end: subtitleNewEndTime},
        transcriptItem.subtitlesHashmap[sortedIds[itemBehindIndex][0]].startTime,
        transcriptItem.subtitlesHashmap[sortedIds[itemBehindIndex][0]].endTime
      )
    ) {
      const itemBehindId = sortedIds[itemBehindIndex][0];
      const subtitleIdsThatNeedEditing = getIdsInFrontOfTarget(
        sortedIds.map((item) => item[0]),
        itemBehindId
      );
      const durationOfItemBeingResized = subtitle.endTime - subtitle.startTime;

      await subtitle.updateFromObject(
        {
          startTime: firstOverlappingItem.startTime,
          endTime: durationOfItemBeingResized + firstOverlappingItem.startTime,
          hasUserEdited: true,
        },
        {undoable: false}
      );

      for (const id of subtitleIdsThatNeedEditing) {
        void transcriptItem.subtitlesHashmap[id].updateFromObject(
          {
            startTime: transcriptItem.subtitlesHashmap[id].startTime + durationOfItemBeingResized,
            endTime: transcriptItem.subtitlesHashmap[id].endTime + durationOfItemBeingResized,
            hasUserEdited: true,
          },
          {undoable: true}
        );
      }
      return;
    }

    await subtitle.updateFromObject({
      startTime: subtitleNewStartTime,
      endTime: subtitleNewEndTime,
      hasUserEdited: true,
    });
    return;
  }

  const subtitleIdsThatNeedEditing = getIdsInFrontOfTarget(
    sortedIds.map((item) => item[0]),
    overlapIds[0]
  );
  const subtitleDuration = subtitle.endTime - subtitle.startTime;

  if (!subtitleIdsThatNeedEditing || subtitleIdsThatNeedEditing.length === 0) {
    const newEndTime = Math.min(firstOverlappingItem.endTime + 0.01 + subtitleDuration, pageDuration);
    await subtitle.updateFromObject({
      startTime: firstOverlappingItem.endTime + 0.01,
      endTime: newEndTime,
      hasUserEdited: true,
    });

    await transcriptItem.page.updateFromObject({duration: newEndTime}, {undoable: false});
    return;
  }

  const isOverlappingWithNextItem = isOverlapping(
    {start: firstOverlappingItem.endTime + 0.01, end: firstOverlappingItem.endTime + 0.01 + subtitleDuration},
    transcriptItem.subtitlesHashmap[subtitleIdsThatNeedEditing[0]].startTime,
    transcriptItem.subtitlesHashmap[subtitleIdsThatNeedEditing[0]].endTime
  );

  const overlappingValue = transcriptItem.subtitlesHashmap[subtitleIdsThatNeedEditing[0]].startTime - firstOverlappingItem.endTime + 0.01 + subtitleDuration;
  await subtitle.updateFromObject({
    startTime: firstOverlappingItem.endTime + 0.01,
    endTime: firstOverlappingItem.endTime + 0.01 + subtitleDuration,
    hasUserEdited: true,
  });

  if (isOverlappingWithNextItem) {
    let lastEndTime: number | null = null;
    for (const id of subtitleIdsThatNeedEditing) {
      const currentItem = transcriptItem.subtitlesHashmap[id];
      lastEndTime = currentItem.endTime + overlappingValue;
      void currentItem.updateFromObject(
        {
          startTime: currentItem.startTime + overlappingValue,
          endTime: Math.min(currentItem.endTime + overlappingValue, pageDuration),
          hasUserEdited: true,
        },
        {undoable: false}
      );
    }

    void transcriptItem.page.updateFromObject({duration: Math.min(lastEndTime ?? pageDuration, pageDuration)}, {undoable: false});
  }
};

export function getIdsInFrontOfTarget(ids: string[], targetId: string): string[] {
  const targetIndex = ids.indexOf(targetId);
  if (targetIndex === -1 || targetIndex === ids.length - 1) {
    return [];
  }

  return ids.slice(targetIndex + 1);
}

export const findOverlappingPercentageWithRespectToStart = (startTime: number, endTime: number, range: Range): number => {
  return ((range.start - startTime) * 100) / (endTime - startTime);
};

const updateAudioData = async (activeItemId: string, obj: DeepPartial<AudioItemObject>, undoable = true): Promise<void> => {
  await window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap[activeItemId].updateFromObject(obj, {
    undoable,
  });
};

export const onAudioItemResizeEnd = async (updatedRange: Range, activeItemId: string): Promise<void> => {
  const audioData = window.posterEditor?.whiteboard?.audioClips;
  let newOnPosterStartTime = updatedRange.start;
  const posterDuration = window.posterEditor?.whiteboard?.getDuration();

  if (!audioData || !posterDuration) {
    return;
  }

  const audioDuration =
    (audioData.audioItemsHashMap[activeItemId].audioPlayer.trim.endTime - audioData.audioItemsHashMap[activeItemId].audioPlayer.trim.startTime) /
    audioData.audioItemsHashMap[activeItemId].audioPlayer.speed;

  if (newOnPosterStartTime > posterDuration) {
    newOnPosterStartTime = posterDuration - audioDuration;
    await updateAudioData(activeItemId, {
      onPosterStartTime: Number(newOnPosterStartTime.toFixed(2)),
    });
    return;
  }

  if (newOnPosterStartTime < 0) {
    newOnPosterStartTime = 0;
  }

  let objectForUpdate: DeepPartial<AudioItemObject> = {
    onPosterStartTime: newOnPosterStartTime,
  };

  if (audioDuration + newOnPosterStartTime > posterDuration) {
    const newEndTime =
      (posterDuration - newOnPosterStartTime + audioData.audioItemsHashMap[activeItemId].audioPlayer.trim.startTime) * audioData.audioItemsHashMap[activeItemId].audioPlayer.speed;
    const newDuration = (newEndTime - audioData.audioItemsHashMap[activeItemId].audioPlayer.trim.startTime) / audioData.audioItemsHashMap[activeItemId].audioPlayer.speed;

    if (newDuration < 1) {
      objectForUpdate = {
        onPosterStartTime: Number((posterDuration - 1 / audioData.audioItemsHashMap[activeItemId].audioPlayer.speed).toFixed(2)),
        audioPlayer: {
          trim: {
            isTrimmed: true,
            endTime: Number((audioData.audioItemsHashMap[activeItemId].audioPlayer.trim.startTime + 1).toFixed(2)),
          },
        },
      };
    } else {
      objectForUpdate = {
        onPosterStartTime: Number(newOnPosterStartTime.toFixed(2)),
        audioPlayer: {
          trim: {
            isTrimmed: true,
            endTime: Number(newEndTime.toFixed(2)),
          },
        },
      };
    }
  }

  await updateAudioData(activeItemId, objectForUpdate);
};
